import { useRef } from "react";
import axios from "axios";
import {
  getSessionToken,
  getSessionTemplateToken,
  getSessionCompanies,
} from "utils/session";
import { useToast } from "./useToast";
import { useAPIToast } from "hooks/useAPIToast";

const AUTHENTICATION_BASE_URL =
  process.env.REACT_APP_AUTHENTICATE ??
  "https://authentication-builder.mixfiscal.com.br/v1";

const TEMPLATE_BASE_URL =
  process.env.REACT_APP_TEMPLATE ||
  "https://templates-builder.mixfiscal.com.br/api";

const NFE_BASE_URL =
  process.env.REACT_APP_NFE_API_XML ?? "https://api-xml.mixfiscal.com.br/api";

const API_ROBOT =
  process.env.REACT_APP_MIX_API ??
  "https://api.interno.mixfiscal.com.br/interno/v1/";

const WHATSAPP_URL =
  process.env.REACT_APP_API_WHATSAPP_INTEGRATION ||
  "https://api.builder.mixfiscal.com.br/app-integration";

const MIX_PRICING_URL =
  process.env.REACT_APP_MIX_PRICING_URL ||
  "https://api.builder.mixfiscal.com.br/app-integration";

const AUTHENTICATION_JOBS_URL =
  process.env.REACT_APP_MIX_PRODUCTS_CONSUT ||
  "https://dev.authentication.mixfiscal.com.br/jobs";

const NFE_FADEL_BASE_URL =
  process.env.REACT_APP_NFE_FADEL ?? "https://nfe.fadel.mixfiscal.com.br";

export const useAPIAuthenticate = () => {
  // TODO: Adicionar toast automatico quando as requisições falharem!
  const onRejected = error => {
    // if (error.toJSON().message === "Network Error") {
    //   addToast({
    //     type: "alert",
    //     description:
    //       "Não foi possível se conectar a internet. Verifique sua conexão",
    //   });
    // }

    return Promise.reject(error);
  };

  const apiRef = useRef(
    axios.create({
      baseURL: AUTHENTICATION_BASE_URL,
    }),
  );

  apiRef.current.interceptors.request.use(config => {
    config.headers.Authorization = getSessionToken();
    if (getSessionCompanies() !== undefined) {
      config.headers["X-token-user"] = getSessionCompanies();
    }
    return config;
  });

  apiRef.current.interceptors.response.use(response => response, onRejected);

  return apiRef.current;
};

export const useAPIAuthenticateToast = apiRef => {
  const api = useAPIAuthenticate();

  const { isLoading, data, onSend } = useAPIToast(
    apiRef ?? api,
    data => data.response ?? data.success,
  );

  return {
    isLoading,
    onSend,
    data,
  };
};

export const useAPINFE2 = () => {
  const onRejected = error => {
    return Promise.reject(error);
  };

  const apiRef = useRef(
    axios.create({
      baseURL: NFE_BASE_URL,
    }),
  );

  apiRef.current.interceptors.request.use(config => {
    config.headers.Authorization = `Bearer ${getSessionCompanies()}`;
    return config;
  });

  apiRef.current.interceptors.response.use(response => response, onRejected);

  return apiRef.current;
};

export const useApiNfeFadel = () => {
  const onRejected = error => {
    return Promise.reject(error);
  };

  const apiRef = useRef(
    axios.create({
      baseURL: NFE_FADEL_BASE_URL,
      withCredentials: false,
    }),
  );
  apiRef.current.interceptors.response.use(response => response, onRejected);

  return apiRef.current;
};

export const useAPIRobot = () => {
  const onRejected = error => {
    return Promise.reject(error);
  };

  const apiRef = useRef(
    axios.create({
      baseURL: API_ROBOT,
    }),
  );

  apiRef.current.interceptors.request.use(config => {
    config.headers.Authorization = `Bearer ${getSessionToken()}`;
    return config;
  });

  apiRef.current.interceptors.response.use(response => response, onRejected);

  return apiRef.current;
};

export const useAPIAuthJob = () => {
  const onRejected = error => {
    return Promise.reject(error);
  };

  const apiRef = useRef(
    axios.create({
      baseURL: AUTHENTICATION_JOBS_URL,
    }),
  );

  apiRef.current.interceptors.request.use(config => {
    config.headers.Authorization = getSessionCompanies();
    return config;
  });

  apiRef.current.interceptors.response.use(response => response, onRejected);

  return apiRef.current;
};

export const useAPINFE2Toast = apiRef => {
  const api = useAPINFE2();

  const { isLoading, data, onSend } = useAPIToast(
    apiRef ?? api,
    data => data.response ?? data.success,
  );

  return {
    isLoading,
    data,
    onSend,
  };
};

export const useAPI = () => {
  const { addToast } = useToast();

  const onRejected = error => {
    addToast({
      type: "error",
      title: "Falha",
      description: `Falha na requisição`,
    });

    return Promise.reject(error);
  };

  const apiRef = useRef(
    axios.create({
      baseURL: TEMPLATE_BASE_URL,
    }),
  );

  apiRef.current.interceptors.request.use(config => {
    config.headers.Authorization = getSessionTemplateToken();
    return config;
  });

  apiRef.current.interceptors.response.use(response => response, onRejected);

  return apiRef.current;
};

export const useAPIWhatsApp = () => {
  const { addToast } = useToast();

  // TODO: Adicionar toast automatico quando as requisições falharem!
  const onRejected = error => {
    if (error.toJSON().message === "Network Error") {
      addToast({
        type: "alert",
        description:
          "Não foi possível se conectar a internet. Verifique sua conexão",
      });
    }

    return Promise.reject(error);
  };

  const apiRef = useRef(
    axios.create({
      baseURL: WHATSAPP_URL,
    }),
  );

  apiRef.current.interceptors.request.use(config => {
    config.headers.Authorization = getSessionToken();
    if (getSessionCompanies() !== undefined) {
      config.headers["X-token-user"] = getSessionCompanies();
    }
    return config;
  });

  apiRef.current.interceptors.response.use(response => response, onRejected);

  return apiRef.current;
};

export const useAPIDiagnostic = () => {
  const baseURL = process.env.REACT_APP_DIAGNOSTICO_API;
  const axiosInstance = axios.create({ baseURL });

  const axiosRef = useRef(axiosInstance);

  axiosRef.current.interceptors.request.use(config => {
    config.headers.Authorization = `Bearer ${getSessionCompanies()}`;
    return config;
  });

  return axiosRef.current;
};

export const useAPIDiagnosticToast = apiRef => {
  const api = useAPIDiagnostic();

  const { isLoading, data, onSend } = useAPIToast(
    apiRef ?? api,
    data => data.response ?? data.success,
  );

  return {
    isLoading,
    data,
    onSend,
  };
};

export const useAPIMixPricing = ({ version } = { version: "v1" }) => {
  const { addToast } = useToast();

  // TODO: Adicionar toast automatico quando as requisições falharem!
  const onRejected = error => {
    if (error.toJSON().message === "Network Error") {
      addToast({
        type: "alert",
        description:
          "Não foi possível se conectar a internet. Verifique sua conexão",
      });
    }

    return Promise.reject(error);
  };

  const apiRef = useRef(
    axios.create({
      baseURL: `${MIX_PRICING_URL}/${version}`,
    }),
  );

  apiRef.current.interceptors.request.use(config => {
    config.headers.Authorization = getSessionToken();
    if (getSessionCompanies() !== undefined) {
      config.headers["X-token-user"] = getSessionCompanies();
    }

    return config;
  });

  apiRef.current.interceptors.response.use(response => response, onRejected);

  return apiRef.current;
};

export const useAPIAuthenticateJob = apiRef => {
  const api = useAPIAuthJob();

  const { isLoading, data, onSend } = useAPIToast(
    apiRef ?? api,
    data => data.response ?? data.success,
  );

  return {
    isLoading,
    data,
    onSend,
  };
};
